/**
 * MemorialBatchImportPage — /admin/memorials/batch-import
 *
 * Paste-one, repeat flow (per Sep 2026 user choice):
 *   1. Paste an obituary (English or Malayalam, any format)
 *   2. Click "Parse with AI" — GPT-5.2 fills all fields
 *   3. Review + edit fields, drop a portrait photo
 *   4. Submit → creates the memorial via the existing quick-add pipeline
 *   5. "Add another" resets everything for the next paste
 *
 * The page is signed-in-user gated (not admin-only) so families can
 * batch-add their own community's obituaries. Entries flow through the
 * regular moderation queue when submitted by non-admins.
 */
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Upload, Sparkles, PlusCircle, Check, X as XIcon, FileText } from "lucide-react";

const P = {
  ink: "#1F1A15",
  inkSoft: "#5E4E3A",
  paper: "#FFF9EF",
  gold: "#B58936",
  saffron: "#B25D38",
  tulsi: "#0E7C55",
  card: "#FFFDF6",
};

const EMPTY_FORM = {
  full_name: "",
  name_ml: "",
  preferred_name: "",
  age_at_death: "",
  death_date: "",
  house_name: "",
  city: "",
  state: "Kerala",
  district: "",
  short_bio: "",
  funeral_date: "",
  funeral_venue: "",
  body_arrival: "",
  faith: "",
};

function Field({ label, testid, value, onChange, type = "text", multiline = false, mono = false, lang, placeholder }) {
  const Cmp = multiline ? "textarea" : "input";
  return (
    <label className="block text-xs">
      <span className="font-mono uppercase tracking-wider opacity-70">{label}</span>
      <Cmp
        type={type}
        value={value}
        lang={lang}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        rows={multiline ? 4 : undefined}
        className="mt-1 w-full px-3 py-2 rounded-lg border text-sm"
        style={{
          borderColor: "#D9C79A",
          background: "white",
          color: P.ink,
          fontFamily: mono ? "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif" : undefined,
        }}
        data-testid={testid}
      />
    </label>
  );
}

export default function MemorialBatchImportPage() {
  const { user, loading: authLoading } = useAuth() || {};
  const [raw, setRaw] = useState("");
  const [parsing, setParsing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photo, setPhoto] = useState(null); // File
  const [photoPreview, setPhotoPreview] = useState(null); // dataURL
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { slug, id, title }

  const hasForm = useMemo(() => Object.values(form).some((v) => (v || "").toString().trim().length), [form]);

  if (authLoading) return <div className="min-h-[60vh] flex items-center justify-center" style={{ background: P.paper }}><Loader2 className="w-6 h-6 animate-spin" style={{ color: P.saffron }} /></div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent("/admin/memorials/batch-import")}`} replace />;

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const parseWithAi = async () => {
    if (raw.trim().length < 10) {
      toast.error("Paste at least a sentence of obituary text.");
      return;
    }
    setParsing(true);
    try {
      const { data } = await http.post("/memorials/parse-obituary-text", { text: raw });
      const p = data.parsed || {};
      setForm({
        full_name:      p.full_name || "",
        name_ml:        p.name_ml || "",
        preferred_name: p.preferred_name || "",
        age_at_death:   p.age_at_death != null ? String(p.age_at_death) : "",
        death_date:     p.death_date || "",
        house_name:     p.house_name || "",
        city:           p.location?.city || "",
        state:          p.location?.state || "Kerala",
        district:       p.location?.district || "",
        short_bio:      p.short_bio || "",
        funeral_date:   p.service_info?.funeral_date || "",
        funeral_venue:  p.service_info?.funeral_venue || "",
        body_arrival:   p.service_info?.body_arrival || "",
        faith:          p.service_info?.faith || "",
      });
      toast.success(`Parsed with ${data.model}. Review + edit before submitting.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI parse failed. Fill fields manually.");
    } finally {
      setParsing(false);
    }
  };

  const onPhoto = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo must be under 8 MB.");
      return;
    }
    setPhoto(file);
    const r = new FileReader();
    r.onload = (e) => setPhotoPreview(e.target?.result || null);
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.full_name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      // Build a friendly title — include the name_ml so the admin
      // moderation list surfaces the original script.
      const title = form.name_ml ? `${form.full_name} (${form.name_ml})` : form.full_name;
      fd.append("title", title);
      fd.append("country", "India");
      fd.append("state", form.state || "");
      fd.append("city", form.city || "");
      fd.append("date", form.death_date || "");
      // Body: preserve the parsed remembrance + service block so the
      // memorial page renders both.
      const bodyLines = [
        form.short_bio,
        form.house_name ? `House / branch: ${form.house_name}` : "",
        form.age_at_death ? `Age: ${form.age_at_death}` : "",
        form.preferred_name ? `Known as: ${form.preferred_name}` : "",
        form.body_arrival ? `Body arrival: ${form.body_arrival}` : "",
        form.funeral_date ? `Funeral: ${form.funeral_date}` : "",
        form.funeral_venue ? `Venue: ${form.funeral_venue}` : "",
        form.faith ? `Faith: ${form.faith}` : "",
      ].filter(Boolean);
      fd.append("description", bodyLines.join("\n"));
      fd.append("notes", `Batch-import via /admin/memorials/batch-import • raw source captured in submission log`);
      if (photo) fd.append("photo", photo);

      // Public endpoint — signed-in users go through moderation.
      const { data } = await http.post("/memorials/quick-add", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult({ slug: data.slug, id: data.id, title });
      toast.success(`Saved. Moderation queue will publish it soon.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save memorial. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const addAnother = () => {
    setRaw("");
    setForm(EMPTY_FORM);
    setPhoto(null);
    setPhotoPreview(null);
    setLastResult(null);
  };

  return (
    <div className="min-h-screen" style={{ background: P.paper, color: P.ink }}>
      <Helmet>
        <title>Batch import memorials · IndianFamilyTree</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>Batch import</div>
            <h1 className="mt-1 font-display italic text-3xl sm:text-4xl leading-tight" data-testid="batch-import-h1">
              Add many memorials, one paste at a time
            </h1>
            <p className="mt-2 max-w-2xl text-sm" style={{ color: P.inkSoft }}>
              Paste any obituary — English, Malayalam, or a mix. GPT-5.2 will fill the fields; you review, drop a photo, and submit. Your entries queue for admin approval unless you're a super admin.
            </p>
          </div>
          <Link
            to="/celebrate/submit"
            className="text-xs font-mono uppercase tracking-widest underline"
            style={{ color: P.saffron }}
            data-testid="batch-import-single-link"
          >
            Prefer the single-form? →
          </Link>
        </div>

        {/* ── Success card ───────────────────────────────────────── */}
        {lastResult && (
          <div
            className="mt-6 rounded-2xl border-2 p-5 flex flex-wrap items-center gap-4"
            style={{ borderColor: `${P.tulsi}66`, background: `${P.tulsi}0F` }}
            data-testid="batch-import-success"
          >
            <Check className="w-6 h-6" style={{ color: P.tulsi }} />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg" style={{ color: P.ink }}>Saved: {lastResult.title}</div>
              <div className="mt-1 text-xs flex flex-wrap gap-3">
                <Link to={`/memorial/${lastResult.slug}`} className="underline" style={{ color: P.saffron }} data-testid="batch-import-view-memorial">View memorial page →</Link>
                <Link to={`/obituary/${lastResult.slug}`} className="underline" style={{ color: P.saffron }} data-testid="batch-import-view-obituary">View obituary notice →</Link>
              </div>
            </div>
            <button
              type="button"
              onClick={addAnother}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
              style={{ background: P.tulsi }}
              data-testid="batch-import-add-another"
            >
              <PlusCircle className="w-4 h-4" /> Add another
            </button>
          </div>
        )}

        {/* ── Paste + parse ──────────────────────────────────────── */}
        <div className="mt-6 rounded-2xl border-2 p-5" style={{ borderColor: `${P.gold}44`, background: P.card }}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>
              <FileText className="w-3.5 h-3.5" /> Step 1 — Paste obituary text
            </div>
            <button
              type="button"
              onClick={parseWithAi}
              disabled={parsing || raw.trim().length < 10}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90"
              style={{ background: P.saffron }}
              data-testid="batch-import-parse-btn"
            >
              {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {parsing ? "Parsing…" : "Parse with AI"}
            </button>
          </div>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={6}
            placeholder={"Paste anywhere from a single-line notice up to a full poster's text.\n\nExample: റോസമ്മ ജോസഫ് (94) പൊടിമറ്റം കിഴക്കേത്തലയ്ക്കൽ. സംസ്കാരം തിങ്കൾ 21-09-2026 10:30 am…"}
            className="mt-3 w-full p-3 rounded-lg border text-sm"
            style={{
              borderColor: "#D9C79A",
              background: "white",
              fontFamily: "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif",
              lineHeight: 1.55,
            }}
            data-testid="batch-import-textarea"
          />
        </div>

        {/* ── Fields ─────────────────────────────────────────────── */}
        <div className="mt-6 rounded-2xl border-2 p-5" style={{ borderColor: `${P.gold}44`, background: P.card }}>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>
            <Sparkles className="w-3.5 h-3.5" /> Step 2 — Review + edit fields
          </div>
          <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2">
            <Field label="Full name (Latin)" testid="batch-import-full-name" value={form.full_name} onChange={upd("full_name")} placeholder="Rosamma Joseph" />
            <Field label="Malayalam name" testid="batch-import-name-ml" value={form.name_ml} onChange={upd("name_ml")} mono lang="ml" placeholder="റോസമ്മ ജോസഫ്" />
            <Field label="Preferred / nickname" testid="batch-import-preferred" value={form.preferred_name} onChange={upd("preferred_name")} placeholder="Thommachen" />
            <Field label="Age at death" testid="batch-import-age" value={form.age_at_death} onChange={upd("age_at_death")} type="number" placeholder="94" />
            <Field label="Death date (YYYY-MM-DD)" testid="batch-import-death-date" value={form.death_date} onChange={upd("death_date")} type="date" />
            <Field label="House / branch name" testid="batch-import-house" value={form.house_name} onChange={upd("house_name")} placeholder="Kizhakkethalaykkal" />
            <Field label="City / village" testid="batch-import-city" value={form.city} onChange={upd("city")} placeholder="Podimattam" />
            <Field label="District" testid="batch-import-district" value={form.district} onChange={upd("district")} placeholder="Kottayam" />
            <Field label="State" testid="batch-import-state" value={form.state} onChange={upd("state")} placeholder="Kerala" />
            <Field label="Faith" testid="batch-import-faith" value={form.faith} onChange={upd("faith")} placeholder="Syro-Malabar Catholic" />
            <div className="sm:col-span-2">
              <Field label="Short remembrance / bio" testid="batch-import-bio" value={form.short_bio} onChange={upd("short_bio")} multiline />
            </div>
            <Field label="Body arrival (home)" testid="batch-import-body-arrival" value={form.body_arrival} onChange={upd("body_arrival")} placeholder="Sunday, 20 September 2026, 4:00 pm" />
            <Field label="Funeral date" testid="batch-import-funeral-date" value={form.funeral_date} onChange={upd("funeral_date")} placeholder="Monday, 21 September 2026, 10:30 am" />
            <div className="sm:col-span-2">
              <Field label="Funeral venue" testid="batch-import-funeral-venue" value={form.funeral_venue} onChange={upd("funeral_venue")} placeholder="Podimattam St. Mary's Church cemetery" />
            </div>
          </div>
        </div>

        {/* ── Photo ──────────────────────────────────────────────── */}
        <div className="mt-6 rounded-2xl border-2 p-5" style={{ borderColor: `${P.gold}44`, background: P.card }}>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>
            <Upload className="w-3.5 h-3.5" /> Step 3 — Portrait photo (optional)
          </div>
          <label className="mt-3 flex items-center gap-4 flex-wrap cursor-pointer" data-testid="batch-import-photo-drop">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onPhoto(e.target.files?.[0])}
              data-testid="batch-import-photo-input"
            />
            {photoPreview ? (
              <img src={photoPreview} alt="preview" className="w-24 h-24 rounded-lg object-cover border-2" style={{ borderColor: P.gold }} />
            ) : (
              <div className="w-24 h-24 rounded-lg flex items-center justify-center border-2 border-dashed" style={{ borderColor: P.gold, background: "#FFFDF6" }}>
                <Upload className="w-6 h-6" style={{ color: P.saffron }} />
              </div>
            )}
            <span className="text-sm" style={{ color: P.inkSoft }}>
              {photo ? photo.name : "Click to choose a photo (JPG/PNG, up to 8 MB)"}
            </span>
            {photo && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setPhoto(null); setPhotoPreview(null); }}
                className="text-xs underline"
                style={{ color: P.saffron }}
                data-testid="batch-import-photo-clear"
              >
                <XIcon className="w-3 h-3 inline" /> Clear
              </button>
            )}
          </label>
        </div>

        {/* ── Submit bar ─────────────────────────────────────────── */}
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={addAnother}
            disabled={!hasForm && !raw && !photo}
            className="text-xs font-mono uppercase tracking-widest px-4 py-2 rounded-full border disabled:opacity-50"
            style={{ borderColor: P.gold, color: P.saffronDeep }}
            data-testid="batch-import-reset"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !form.full_name.trim()}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white text-sm font-semibold shadow disabled:opacity-50 hover:opacity-90"
            style={{ background: P.tulsi }}
            data-testid="batch-import-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {submitting ? "Saving…" : "Save memorial"}
          </button>
        </div>
      </div>
    </div>
  );
}
